import { getApiError } from '@/composables/getApiError'
import { BASE_URL } from '@/environment/environment'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { logger } from '@/utils/eventLogger'
import { isHttpNotFound } from '@/utils/httpErrors'
import axios from 'axios'
import { defineStore } from 'pinia'

import type { IAuditNonconformityFormStoreState } from './types/IAuditNonconformityFormStoreState'
import type { IAuditorReportNotComplyDetail, ICorrectiveActionFinding } from './types/IAuditorReportNotComplyDetail'

const reportBase = (reportId: number) => `${BASE_URL}/api/audit/reports/not-comply/${reportId}`
const findingEntriesBase = (findingId: number) => `${BASE_URL}/api/audit/reports/not-comply/findings/${findingId}/responsibility-entries`

export const useAuditNonconformityFormStore = defineStore('auditNonconformityForm', {
  state: (): IAuditNonconformityFormStoreState => ({
    currentNonconformity: null,
    editableNonconformity: null,
    activeFindingIndex: 0,
  }),

  getters: {
    isDirty(state): boolean {
      if (!state.currentNonconformity || !state.editableNonconformity) return false

      return JSON.stringify(state.currentNonconformity) !== JSON.stringify(state.editableNonconformity)
    },

    currentFinding(state): ICorrectiveActionFinding | null {
      const findings = state.currentNonconformity?.findings || []
      if (!findings.length) return null

      return findings[state.activeFindingIndex] ?? findings[0] ?? null
    },
  },

  actions: {
    setActiveFindingIndex(index: number) {
      this.activeFindingIndex = index
    },

    async fetchById(reportId: number): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const res = await axios.get(`${reportBase(reportId)}/`)
        const data = res.data as IAuditorReportNotComplyDetail

        this.currentNonconformity = {
          ...data,
          findings: data.findings ?? [],
          files: data.files ?? [],
          actions: data.actions ?? [],
          editable_fields: data.editable_fields ?? [],
        }
        this.editableNonconformity = JSON.parse(JSON.stringify(this.currentNonconformity))

        if (this.activeFindingIndex >= this.currentNonconformity.findings.length) {
          this.activeFindingIndex = 0
        }
      } catch (error) {
        logger.error('fetchNonconformityById_error', {
          store: 'auditNonconformityForm',
          function: 'fetchById',
          error: String(error),
          reportId,
        })
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить несоответствие'),
        })
        if (isHttpNotFound(error))
          throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    resetForm() {
      this.currentNonconformity = null
      this.editableNonconformity = null
      this.activeFindingIndex = 0
    },

    syncEditableFromCurrent() {
      if (this.currentNonconformity) {
        this.editableNonconformity = JSON.parse(JSON.stringify(this.currentNonconformity))
      }
    },

    /**
     * Отправить карточку КМ на контроль СМК
     */
    async submitReport(reportId: number): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const res = await axios.post(`${reportBase(reportId)}/submit/`)
        this.currentNonconformity = res.data
        this.syncEditableFromCurrent()
        feedbackStore.showToast({ type: 'success', message: 'Отчёт отправлен на контроль СМК' })
      } catch (error) {
        logger.error('auditNc_submitReport_error', {
          store: 'auditNonconformityForm',
          function: 'submitReport',
          error: String(error),
          reportId,
        })
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось отправить отчёт в СМК'),
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Принять отчёт КМ сотрудником СМК
     */
    async acceptReport(reportId: number, comment: string = ''): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const res = await axios.post(`${reportBase(reportId)}/accept/`, { comment })
        this.currentNonconformity = res.data
        this.syncEditableFromCurrent()
        feedbackStore.showToast({ type: 'success', message: 'Отчёт принят' })
      } catch (error) {
        logger.error('auditNc_acceptReport_error', {
          store: 'auditNonconformityForm',
          function: 'acceptReport',
          error: String(error),
          reportId,
        })
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось принять отчёт'),
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Вернуть отчёт КМ на доработку сотрудником СМК
     */
    async returnReportForRevision(
      reportId: number,
      remarks: { responsibility_entry_id: number; comment: string }[],
      comment: string = '',
    ): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const res = await axios.post(`${reportBase(reportId)}/return-for-revision/`, {
          remarks,
          comment,
        })
        this.currentNonconformity = res.data
        this.syncEditableFromCurrent()
        feedbackStore.showToast({ type: 'success', message: 'Отчёт возвращён на доработку' })
      } catch (error) {
        logger.error('auditNc_returnReportForRevision_error', {
          store: 'auditNonconformityForm',
          function: 'returnReportForRevision',
          error: String(error),
          reportId,
        })
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось вернуть отчёт на доработку'),
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загрузить подписанный скан отчёта КМ
     */
    async uploadReportFile(reportId: number, file: File): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const formData = new FormData()
        formData.append('file', file)

        const res = await axios.post(`${reportBase(reportId)}/upload/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        this.currentNonconformity = res.data
        this.syncEditableFromCurrent()
        feedbackStore.showToast({ type: 'success', message: 'Подписанный отчёт загружен' })
      } catch (error) {
        logger.error('auditNc_uploadReportFile_error', {
          store: 'auditNonconformityForm',
          function: 'uploadReportFile',
          error: String(error),
          reportId,
        })
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить файл отчёта'),
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Скачать печатную форму отчёта КМ (DOCX)
     */
    async printReportDocx(reportId: number): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const response = await axios.get(`${reportBase(reportId)}/print/`, {
          responseType: 'blob',
        })

        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `Отчет_КМ_${reportId}.docx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)

        feedbackStore.showToast({ type: 'success', message: 'Отчёт скачан' })
      } catch (error) {
        logger.error('auditNc_printReportDocx_error', {
          store: 'auditNonconformityForm',
          function: 'printReportDocx',
          error: String(error),
          reportId,
        })
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось скачать печатную форму отчёта'),
        })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Взять задачу пункта в работу исполнителем
     */
    async takeFindingTask(findingId: number, entryId: number, reportId?: number): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        await axios.post(`${findingEntriesBase(findingId)}/${entryId}/in-progress/`)
        if (reportId || this.currentNonconformity?.id) {
          await this.fetchById(reportId || this.currentNonconformity!.id)
        }
        feedbackStore.showToast({ type: 'success', message: 'Взято в работу' })
      } catch (error) {
        logger.error('auditNc_takeFindingTask_error', {
          store: 'auditNonconformityForm',
          function: 'takeFindingTask',
          error: String(error),
          findingId,
          entryId,
        })
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось взять задачу в работу'),
        })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Завершить отработку пункта исполнителем с отчётом
     */
    async completeFindingTask(
      findingId: number,
      entryId: number,
      report: string,
      files?: File[],
      reportId?: number,
    ): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const formData = new FormData()
        formData.append('report', report)
        if (files?.length) {
          files.forEach(file => formData.append('files', file))
        }

        await axios.post(`${findingEntriesBase(findingId)}/${entryId}/complete/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })

        if (reportId || this.currentNonconformity?.id) {
          await this.fetchById(reportId || this.currentNonconformity!.id)
        }
        feedbackStore.showToast({ type: 'success', message: 'Отчёт по пункту отправлен' })
      } catch (error) {
        logger.error('auditNc_completeFindingTask_error', {
          store: 'auditNonconformityForm',
          function: 'completeFindingTask',
          error: String(error),
          findingId,
          entryId,
        })
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось завершить задачу пункта'),
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Обновить исполнителя / задачу пункта (PATCH)
     */
    async updateFindingResponsible(
      findingId: number,
      entryId: number,
      payload: {
        responsible_employee?: { id: string }
        responsible_employee_ids?: string[]
        instructions?: string
        deadline_time?: string | null
        is_high_priority?: boolean
      },
      reportId?: number,
    ): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        await axios.patch(`${findingEntriesBase(findingId)}/${entryId}/`, payload)
        if (reportId || this.currentNonconformity?.id) {
          await this.fetchById(reportId || this.currentNonconformity!.id)
        }
        feedbackStore.showToast({ type: 'success', message: 'Исполнитель назначен' })
      } catch (error) {
        logger.error('auditNc_updateFindingResponsible_error', {
          store: 'auditNonconformityForm',
          function: 'updateFindingResponsible',
          error: String(error),
          findingId,
          entryId,
        })
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось назначить исполнителя'),
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Добавить замечание к задаче пункта (возврат руководителем)
     */
    async addFindingRemark(
      findingId: number,
      entryId: number,
      comment: string,
      reportId?: number,
    ): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        await axios.post(`${findingEntriesBase(findingId)}/${entryId}/remarks/`, { comment })
        if (reportId || this.currentNonconformity?.id) {
          await this.fetchById(reportId || this.currentNonconformity!.id)
        }
        feedbackStore.showToast({ type: 'success', message: 'Замечание отправлено' })
      } catch (error) {
        logger.error('auditNc_addFindingRemark_error', {
          store: 'auditNonconformityForm',
          function: 'addFindingRemark',
          error: String(error),
          findingId,
          entryId,
        })
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось отправить замечание'),
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Обработка realtime-событий записей ответственности (responsibility_entry_*) для открытого несоответствия
     */
    async applyResponsibilityEntryRealtime(payload: any): Promise<void> {
      if (!payload?.event_type || !payload?.data) return
      const entry = payload.data as any
      if (entry.source_type !== 'auditorreport' && entry.source_type !== 'correctiveactionreport') return

      const reportId = Number(entry.url_props?.reportId)
      if (!reportId || Number(this.currentNonconformity?.id) !== reportId) return

      await this.fetchById(reportId)
    },
  },
})

